import React, { useState } from 'react';
import Editor from '@monaco-editor/react';
import { Play, RotateCcw, Loader } from 'lucide-react';

const CodeEditor = ({ initialCode, onRun, readOnly = false }) => {
  const [code, setCode] = useState(initialCode || '# Write your Python code here\n');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  
  const handleEditorChange = (value) => {
    setCode(value || '');
  };
  
  const handleRun = async () => {
    setIsRunning(true);
    setOutput('');
    setError('');
    
    try {
      const result = await onRun(code);
      
      if (result.success) {
        setOutput(result.output || 'Program executed successfully (no output)');
      } else {
        setError(result.error || 'Execution failed');
        if (result.output) {
          setOutput(result.output);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to execute code');
    } finally {
      setIsRunning(false);
    }
  };
  
  const handleReset = () => {
    setCode(initialCode || '# Write your Python code here\n');
    setOutput('');
    setError('');
  };
  
  return (
    <div className="flex flex-col h-full">
      {/* Editor Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-b border-gray-300">
        <span className="text-sm font-medium text-gray-700">Python Editor</span>
        <div className="flex gap-2">
          <button
            onClick={handleReset}
            className="btn-secondary text-sm py-1 px-3 inline-flex items-center"
            disabled={isRunning}
          >
            <RotateCcw size={16} className="mr-1" />
            Reset
          </button>
          <button
            onClick={handleRun}
            className="btn-primary text-sm py-1 px-3 inline-flex items-center"
            disabled={isRunning || readOnly}
          >
            {isRunning ? (
              <>
                <Loader size={16} className="mr-1 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Play size={16} className="mr-1" />
                Run Code
              </>
            )}
          </button>
        </div>
      </div>
      
      {/* Monaco Editor */}
      <div className="flex-1 min-h-[300px]">
        <Editor
          height="100%"
          defaultLanguage="python"
          value={code}
          onChange={handleEditorChange}
          theme="vs-dark"
          options={{
            minimap: { enabled: false },
            fontSize: 14,
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            automaticLayout: true,
            readOnly: readOnly,
          }}
        />
      </div>
      
      {/* Output Console */}
      <div className="h-48 bg-gray-900 text-gray-100 font-mono text-sm overflow-auto">
        <div className="px-4 py-2 border-b border-gray-700 bg-gray-800">
          <span className="font-semibold">Output</span>
        </div>
        <div className="p-4">
          {output && (
            <pre className="whitespace-pre-wrap text-green-400">{output}</pre>
          )}
          {error && (
            <pre className="whitespace-pre-wrap text-red-400">{error}</pre>
          )}
          {!output && !error && !isRunning && (
            <span className="text-gray-500">Click "Run Code" to see output...</span>
          )}
          {isRunning && (
            <span className="text-yellow-400">Executing code...</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default CodeEditor;




